/**
 * TileStore → GPU 纹理（M3-09）。
 *
 * 执行器要的是«某图层某块的纹理»；这里把它接到真正的像素存储上，按内容
 * 缓存纹理。失效由调用方（GpuCompositor）驱动：它读 `store.pixelDirty`
 * 精确知道哪些块变了，只对那些块调用 {@link invalidateTile}——干净帧
 * 一次 `invalidateTile` 都不调用，缓存全部命中，`stats.uploads` 为 0，
 * 这正是"干净帧零上传"验收标准的实现方式。
 */
import { TILE, layerId, tileKey } from '../../engine/tile/key';
import type { TileStore } from '../../engine/tile/store';
import type { GpuBackend, GpuTexture } from '../../gpu/backend';
import type { TileSource } from '../../gpu/executor/tile-source';

export interface UploadedTileSourceStats {
  /** 本帧实际发生的 writeTexture 次数（缓存未命中数）。调用方每帧读完即清零。 */
  uploads: number;
}

export class UploadedTileSource implements TileSource {
  readonly #backend: GpuBackend;
  readonly #store: TileStore;
  readonly #cache = new Map<string, GpuTexture>();
  readonly #canvas: GpuTexture;
  readonly #empty: GpuTexture;
  readonly stats: UploadedTileSourceStats = { uploads: 0 };

  constructor(backend: GpuBackend, store: TileStore) {
    this.#backend = backend;
    this.#store = store;
    this.#canvas = this.#solid(255, 255, 255, 255);
    this.#empty = this.#solid(0, 0, 0, 0);
  }

  #solid(r: number, g: number, b: number, a: number): GpuTexture {
    const texture = this.#backend.createTexture({ width: TILE, height: TILE, format: 'rgba8unorm' });
    const data = new Uint8Array(TILE * TILE * 4);
    for (let i = 0; i < data.length; i += 4) {
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = a;
    }
    this.#backend.writeTexture(texture, data);
    return texture;
  }

  canvasTexture(): GpuTexture {
    return this.#canvas;
  }

  emptyTexture(): GpuTexture {
    return this.#empty;
  }

  /** 让指定块在下次访问时重新上传（该块像素改动后调用）。 */
  invalidateTile(layer: string, tx: number, ty: number): void {
    const key = `${layer} ${tx} ${ty}`;
    this.#cache.get(key)?.destroy();
    this.#cache.delete(key);
  }

  /** 全部作废（结构性变化：图层增删、undo/redo 换文档引用）。 */
  invalidateAll(): void {
    for (const texture of this.#cache.values()) texture.destroy();
    this.#cache.clear();
  }

  tileFor(layer: string, tx: number, ty: number): GpuTexture | null {
    const key = `${layer} ${tx} ${ty}`;
    const cached = this.#cache.get(key);
    if (cached !== undefined) return cached;

    const tile = this.#store.get(tileKey(layerId(layer), tx, ty));
    if (tile?.data == null) return null;

    const texture = this.#backend.createTexture({ width: TILE, height: TILE, format: 'rgba8unorm' });
    this.#backend.writeTexture(texture, tile.data);
    this.#cache.set(key, texture);
    this.stats.uploads++;
    return texture;
  }

  destroy(): void {
    for (const texture of this.#cache.values()) texture.destroy();
    this.#cache.clear();
    this.#canvas.destroy();
    this.#empty.destroy();
  }
}
